import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, styled } from '@mui/material';
import { AircraftDataGridProps, AircraftData, DEFAULT_AIRCRAFT_CONFIGURATIONS } from '../types/ESALTypesAircraft';
import { ESALFormulas } from '../ESALFormulas';
import EsalNumberCell from './EsalNumberCell';

// Styled components for table styling
const StyledTableContainer = styled(TableContainer)(() => ({
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: 500,
    width: '100%',
}));

const StyledTableHead = styled(TableHead)(() => ({
    backgroundColor: '#f5f5f5',
    '& .MuiTableCell-head': {
        fontWeight: 'bold',
        fontSize: '0.875rem',
        padding: '12px 8px',
        borderRight: '1px solid #e0e0e0',
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&:hover': {
        backgroundColor: '#f9f9f9',
    },
    '& .MuiTableCell-body': {
        padding: '8px',
        borderRight: '1px solid #e0e0e0',
        fontSize: '0.875rem',
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const EditableTableCell = styled(TableCell)(() => ({
    backgroundColor: '#fff3e0',
    '& .MuiTextField-root': {
        '& input': {
            padding: '4px 8px',
            fontSize: '0.875rem',
        },
    },
}));

const ResultTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    color: theme.palette.success.main,
}));

/**
 * Aircraft data grid component with inline editing capabilities
 * Columns in exact order: SELECT, AIRCRAFT, Axles, RUNWAY 1, RUNWAY 2, USERS TOTAL LOAD, DESIGN TOTAL LOAD,
 * Load percentages, Axle loads, Equivalent EALF, ESAL/day
 */
const AircraftDataGrid: React.FC<AircraftDataGridProps> = ({ aircraft, onAircraftUpdate, isEditable, projectData, onValidityChange, resetKey }) => {
    const { t } = useTranslation();

    /**
     * Validate that load percentages sum to 100%
     */
    const validatePercentages = useCallback((aircraft: AircraftData): boolean => {
        const total = aircraft.steeringLoadPercentage + aircraft.singleLoadPercentage + aircraft.tandemLoadPercentage + aircraft.tridemLoadPercentage;
        return Math.abs(total - 100) < 0.01; // Allow small floating point errors
    }, []);

    /**
     * Calculate aircraft ESAL using single vehiclesPerDay field
     */
    const calculateAircraftESAL = useCallback((aircraft: AircraftData): AircraftData => {
        // Sum movements from both runways
        const totalMovements = (aircraft.runway1Vehicles || 0) + (aircraft.runway2Vehicles || 0);

        if (!aircraft.selected || totalMovements <= 0) {
            return {
                ...aircraft,
                ealfSteeringAxle: 0,
                ealfSingleAxle: 0,
                ealfTandemAxle: 0,
                ealfTridemAxle: 0,
                equivalentEalfPerVehicle: 0,
                esalRunway1: 0,
                esalRunway2: 0,
                esalPerDay: 0,
                passagePercentage: totalMovements > 0 ? 100 : 0,
            };
        }

        // Get aircraft configuration from default configurations
        const config = DEFAULT_AIRCRAFT_CONFIGURATIONS.find(c => c.id === aircraft.id);
        if (!config) {
            console.error(`Configuration not found for aircraft ${aircraft.id}`);
            return { ...aircraft, esalPerDay: 0 };
        }

        const structuralNumber = ESALFormulas.CONSTANTS.STRUCTURAL_NUMBER;

        // Convert constants (extracted from ESALFormulas.EXCEL_CONSTANTS)
        const KN_TO_KIPS = 0.224809;
        const GT = -0.20091484278071334;
        const BETA_18 = 0.5000585397376548;
        const T8 = 1; // Steering axle constant
        const X8 = 2; // Tandem axle constant
        const Z8 = 3; // Tridem axle constant

        // Excel-compatible rounding function
        const roundToExcelPrecision = (value: number, decimals: number): number => {
            if (!isFinite(value)) return value;
            const factor = Math.pow(10, decimals);
            return Math.round(value * factor) / factor;
        };

        // Calculate load per axle (aircraft loads are already per-axle from configuration)
        const steeringLoadPerAxleKips = aircraft.steeringAxleLoad * KN_TO_KIPS;
        const tandemLoadKips = aircraft.tandemAxleLoad * KN_TO_KIPS;
        const tridemLoadKips = aircraft.tridemAxleLoad * KN_TO_KIPS;

        // Calculate individual EALF values for internal calculations (not displayed)
        let ealfSteeringAxle = 0;
        let ealfSingleAxle = 0;
        let ealfTandemAxle = 0;
        let ealfTridemAxle = 0;

        // Steering EALF calculation
        if (steeringLoadPerAxleKips > 0 && aircraft.steeringAxles > 0) {
            const betaX = 0.4 + (0.081 * Math.pow(steeringLoadPerAxleKips + T8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(T8, 3.23));
            const ealf = 1 / (Math.pow(19 / (steeringLoadPerAxleKips + T8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(T8, 4.33));
            ealfSteeringAxle = ealf;
        }

        // Single axle EALF calculation (use correct AASHTO formula)
        const singleLoadKips = aircraft.singleAxleLoad * KN_TO_KIPS;
        if (singleLoadKips > 0 && aircraft.singleAxles > 0) {
            const V8 = 1; // Single axle constant (same as steering)
            const betaX = 0.4 + (0.081 * Math.pow(singleLoadKips + V8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(V8, 3.23));
            const ealf = 1 / (Math.pow(19 / (singleLoadKips + V8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(V8, 4.33));
            ealfSingleAxle = ealf;
        }

        // Tandem EALF calculation
        if (tandemLoadKips > 0 && aircraft.tandemAxles > 0) {
            const betaX = 0.4 + (0.081 * Math.pow(tandemLoadKips + X8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(X8, 3.23));
            const ealf = 1 / (Math.pow(19 / (tandemLoadKips + X8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(X8, 4.33));
            ealfTandemAxle = ealf;
        }

        // Tridem EALF calculation
        if (tridemLoadKips > 0 && aircraft.tridemAxles > 0) {
            const betaX = 0.4 + (0.081 * Math.pow(tridemLoadKips + Z8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(Z8, 3.23));
            const ealf = 1 / (Math.pow(19 / (tridemLoadKips + Z8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(Z8, 4.33));
            ealfTridemAxle = ealf;
        }

        // Calculate equivalent EALF per vehicle (sum of all EALFs - keep full precision like Excel)
        const equivalentEalfPerVehicle = ealfSteeringAxle + ealfSingleAxle + ealfTandemAxle + ealfTridemAxle;

        // Calculate ESAL per runway separately (keep full precision like Excel)
        const esalRunway1 = (aircraft.runway1Vehicles || 0) * equivalentEalfPerVehicle;
        const esalRunway2 = (aircraft.runway2Vehicles || 0) * equivalentEalfPerVehicle;

        // Calculate total ESAL per day (sum of both runways - keep full precision)
        const esalPerDay = esalRunway1 + esalRunway2;

        return {
            ...aircraft,
            ealfSteeringAxle,
            ealfSingleAxle,
            ealfTandemAxle,
            ealfTridemAxle,
            equivalentEalfPerVehicle,
            esalRunway1,
            esalRunway2,
            esalPerDay,
            passagePercentage: 100,
        };
    }, []);

    /**
     * Handle checkbox selection changes
     */
    const handleSelectionChange = useCallback(
        (aircraftId: number, selected: boolean) => {
            const aircraftItem = aircraft.find(a => a.id === aircraftId);
            if (aircraftItem) {
                const updatedAircraft = { ...aircraftItem, selected };
                // Recalculate ESAL when selection changes
                const recalculatedAircraft = calculateAircraftESAL(updatedAircraft);
                onAircraftUpdate(recalculatedAircraft);
            }
        },
        [aircraft, onAircraftUpdate, calculateAircraftESAL]
    );

    /**
     * Handle cell value changes with real-time calculation updates
     */
    const handleCellValueChange = useCallback(
        (aircraftId: number, field: string, numericValue: number) => {
            const aircraftData = aircraft.find(a => a.id === aircraftId);
            if (!aircraftData) return;

            // Update the aircraft data
            let updatedAircraft = { ...aircraftData, [field]: numericValue };

            // If usersTotalLoad, any percentage, or axle count is being changed, recalculate axle loads dynamically
            const isCalculationChange =
                field === 'usersTotalLoad' ||
                field === 'steeringLoadPercentage' ||
                field === 'singleLoadPercentage' ||
                field === 'tandemLoadPercentage' ||
                field === 'tridemLoadPercentage' ||
                field === 'steeringAxles' ||
                field === 'singleAxles' ||
                field === 'tandemAxles' ||
                field === 'tridemAxles';

            if (isCalculationChange) {
                const config = DEFAULT_AIRCRAFT_CONFIGURATIONS.find(c => c.id === aircraftId);
                if (config) {
                    // Use current weight (usersTotalLoad or designTotalLoad if users is 0)
                    const effectiveWeight = field === 'usersTotalLoad' ? numericValue : updatedAircraft.usersTotalLoad || updatedAircraft.designTotalLoad;

                    // Create config object with current values (use updated value if changed, otherwise current aircraft data)
                    const currentConfig = {
                        steeringAxles: field === 'steeringAxles' ? numericValue : updatedAircraft.steeringAxles,
                        singleAxles: field === 'singleAxles' ? numericValue : updatedAircraft.singleAxles,
                        tandemAxles: field === 'tandemAxles' ? numericValue : updatedAircraft.tandemAxles,
                        tridemAxles: field === 'tridemAxles' ? numericValue : updatedAircraft.tridemAxles,
                        steeringLoadPercentage: field === 'steeringLoadPercentage' ? numericValue : updatedAircraft.steeringLoadPercentage,
                        singleLoadPercentage: field === 'singleLoadPercentage' ? numericValue : updatedAircraft.singleLoadPercentage,
                        tandemLoadPercentage: field === 'tandemLoadPercentage' ? numericValue : updatedAircraft.tandemLoadPercentage,
                        tridemLoadPercentage: field === 'tridemLoadPercentage' ? numericValue : updatedAircraft.tridemLoadPercentage,
                    };

                    if (effectiveWeight > 0) {
                        const dynamicAxleLoads = ESALFormulas.calculateDynamicAxleLoads(effectiveWeight, currentConfig);
                        updatedAircraft = {
                            ...updatedAircraft,
                            steeringAxleLoad: dynamicAxleLoads.steeringAxleLoad,
                            singleAxleLoad: dynamicAxleLoads.singleAxleLoad,
                            tandemAxleLoad: dynamicAxleLoads.tandemAxleLoad,
                            tridemAxleLoad: dynamicAxleLoads.tridemAxleLoad,
                        };
                    }
                }
            }

            // Recalculate ESAL with updated values
            const recalculatedAircraft = calculateAircraftESAL(updatedAircraft);

            // Check percentage validation for visual feedback - only for percentage changes
            const isPercentageChange = field === 'steeringLoadPercentage' || field === 'singleLoadPercentage' || field === 'tandemLoadPercentage' || field === 'tridemLoadPercentage';

            if (isPercentageChange && !validatePercentages(updatedAircraft)) {
                console.warn(`Aircraft ${aircraftId}: Load percentages do not sum to 100%`);
            }

            onAircraftUpdate(recalculatedAircraft);
        },
        [aircraft, onAircraftUpdate, calculateAircraftESAL, validatePercentages]
    );

    /**
     * Calculate total ESAL for all selected aircraft
     */
    const totalESAL = useMemo(() => {
        const sum = aircraft.filter(aircraft => aircraft.selected).reduce((total, aircraft) => total + aircraft.esalPerDay, 0);
        return sum; // Keep full precision for calculations
    }, [aircraft]);

    return (
        <Box sx={{ mb: 3 }}>
            {/* Table header with total */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {t('esal_aircraft_data_title', 'Aircraft Traffic Data')}
                </Typography>
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {t('esal_total_label', 'Total ESAL')}: {ESALFormulas.formatNumber(totalESAL, 2)} {t('esal_unit', 'ESAL/day')}
                </Typography>
            </Box>

            {/* Data table with exact column order */}
            <StyledTableContainer>
                <Table stickyHeader>
                    <StyledTableHead>
                        <TableRow>
                            {/* 1. SELECT */}
                            <TableCell align="center" sx={{ minWidth: 80, width: '6%' }}>
                                {t('esal_table_select', 'Select')}
                            </TableCell>
                            {/* 2. AIRCRAFT */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_aircraft', 'Aircraft')}
                            </TableCell>
                            {/* 3. Number of Axles */}
                            <TableCell align="center" sx={{ minWidth: 100, width: '7%' }}>
                                {t('esal_table_number_of_axles', 'Number of Axles')}
                            </TableCell>
                            {/* 4. RUNWAY 1 */}
                            <TableCell align="center" sx={{ minWidth: 100, width: '7%' }}>
                                {t('esal_table_runway_1', 'RUNWAY 1')}
                            </TableCell>
                            {/* 5. RUNWAY 2 */}
                            <TableCell align="center" sx={{ minWidth: 100, width: '7%' }}>
                                {t('esal_table_runway_2', 'RUNWAY 2')}
                            </TableCell>
                            {/* 6. USERS TOTAL LOAD */}
                            <TableCell align="center" sx={{ minWidth: 140, width: '9%' }}>
                                {t('esal_table_users_total_load', 'USERS TOTAL LOAD (kN)')}
                            </TableCell>
                            {/* 7. DESIGN TOTAL LOAD */}
                            <TableCell align="center" sx={{ minWidth: 140, width: '9%' }}>
                                {t('esal_table_design_total_load', 'DESIGN TOTAL LOAD (kN)')}
                            </TableCell>
                            {/* 8. Steering Axles */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_steering_axles', 'Steering Axles')}
                            </TableCell>
                            {/* 9. Single Axles */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_single_axles', 'Single Axles')}
                            </TableCell>
                            {/* 10. Tandem Axles */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_tandem_axles', 'Tandem Axles')}
                            </TableCell>
                            {/* 11. Tridem Axles */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_tridem_axles', 'Tridem Axles')}
                            </TableCell>
                            {/* 12. Steering Load % */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_steering_load_percentage', 'Steering Load %')}
                            </TableCell>
                            {/* 8. Single Load % */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_single_load_percentage', 'Single Load %')}
                            </TableCell>
                            {/* 9. Tandem Load % */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_tandem_load_percentage', 'Tandem Load %')}
                            </TableCell>
                            {/* 10. Tridem Load % */}
                            <TableCell align="center" sx={{ minWidth: 120, width: '8%' }}>
                                {t('esal_table_tridem_load_percentage', 'Tridem Load %')}
                            </TableCell>

                            {/* 17. Total ESAL/day */}
                            <TableCell align="center" sx={{ minWidth: 130, width: '8%' }}>
                                {t('esal_table_total_esal_per_day', 'Total ESAL/day')}
                            </TableCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {aircraft.map(aircraftData => (
                            <StyledTableRow key={aircraftData.id}>
                                {/* 1. SELECT - Checkbox */}
                                <TableCell align="center">
                                    <Checkbox checked={aircraftData.selected} onChange={e => handleSelectionChange(aircraftData.id, e.target.checked)} size="small" disabled={!isEditable} />
                                </TableCell>

                                {/* 2. AIRCRAFT - Image */}
                                <TableCell align="center">
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <img src={aircraftData.imageSrc.default} alt={aircraftData.name} style={{ height: 32, objectFit: 'contain', display: 'block' }} />
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center', lineHeight: 1 }}>
                                            {aircraftData.name}
                                        </Typography>
                                    </div>
                                </TableCell>

                                {/* 3. Number of Axles - Display only */}
                                <TableCell align="center">
                                    <Typography variant="body2" fontWeight="bold">
                                        {aircraftData.axles}
                                    </Typography>
                                </TableCell>

                                {/* 4. RUNWAY 1 - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:runway1Vehicles`}
                                        fieldLabel={t('esal_table_runway_1', 'RUNWAY 1')}
                                        value={aircraftData.runway1Vehicles}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'runway1Vehicles', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 5. RUNWAY 2 - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:runway2Vehicles`}
                                        fieldLabel={t('esal_table_runway_2', 'RUNWAY 2')}
                                        value={aircraftData.runway2Vehicles}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'runway2Vehicles', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 6. USERS TOTAL LOAD - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:usersTotalLoad`}
                                        fieldLabel={t('esal_table_users_total_load', 'USERS TOTAL LOAD (kN)')}
                                        value={aircraftData.usersTotalLoad}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'usersTotalLoad', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                        placeholder={aircraftData.designTotalLoad.toString()}
                                    />
                                </EditableTableCell>

                                {/* 6. DESIGN TOTAL LOAD - Display only */}
                                <TableCell align="center">
                                    <Typography variant="body2">{ESALFormulas.formatNumber(aircraftData.designTotalLoad, 0)}</Typography>
                                </TableCell>

                                {/* 7. Steering Axles - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:steeringAxles`}
                                        fieldLabel={t('esal_table_steering_axles', 'Steering Axles')}
                                        value={aircraftData.steeringAxles}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'steeringAxles', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 8. Single Axles - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:singleAxles`}
                                        fieldLabel={t('esal_table_single_axles', 'Single Axles')}
                                        value={aircraftData.singleAxles}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'singleAxles', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 9. Tandem Axles - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:tandemAxles`}
                                        fieldLabel={t('esal_table_tandem_axles', 'Tandem Axles')}
                                        value={aircraftData.tandemAxles}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'tandemAxles', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 10. Tridem Axles - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:tridemAxles`}
                                        fieldLabel={t('esal_table_tridem_axles', 'Tridem Axles')}
                                        value={aircraftData.tridemAxles}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'tridemAxles', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 11. Steering Load % - Input 🟡 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:steeringLoadPercentage`}
                                        fieldLabel={t('esal_table_steering_load_percentage', 'Steering Load %')}
                                        value={aircraftData.steeringLoadPercentage}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'steeringLoadPercentage', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 8. Single Load % - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:singleLoadPercentage`}
                                        fieldLabel={t('esal_table_single_load_percentage', 'Single Load %')}
                                        value={aircraftData.singleLoadPercentage}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'singleLoadPercentage', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 9. Tandem Load % - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:tandemLoadPercentage`}
                                        fieldLabel={t('esal_table_tandem_load_percentage', 'Tandem Load %')}
                                        value={aircraftData.tandemLoadPercentage}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'tandemLoadPercentage', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 10. Tridem Load % - Input 🟢 */}
                                <EditableTableCell align="center">
                                    <EsalNumberCell
                                        cellKey={`${aircraftData.id}:tridemLoadPercentage`}
                                        fieldLabel={t('esal_table_tridem_load_percentage', 'Tridem Load %')}
                                        value={aircraftData.tridemLoadPercentage}
                                        onValueChange={value => handleCellValueChange(aircraftData.id, 'tridemLoadPercentage', value)}
                                        onValidityChange={onValidityChange}
                                        resetKey={resetKey}
                                        disabled={!isEditable || !aircraftData.selected}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </EditableTableCell>

                                {/* 17. Total ESAL per day - Calculated */}
                                <ResultTableCell align="center">
                                    <Typography variant="body2" fontWeight="bold">
                                        {aircraftData.selected ? ESALFormulas.formatNumber(aircraftData.esalPerDay, 2) : '0.00'}
                                    </Typography>
                                </ResultTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        </Box>
    );
};

export default AircraftDataGrid;
